// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUPPORT-3 Phase C: the support team's AI assistant.
 * Chat about the product, user experience, and troubleshooting — with an optional
 * ticket number for context. The backend answers with the real model when
 * SUPPORT_AI_KEY is configured; otherwise the curated demo knowledge base replies
 * (clearly badged). The assistant cannot change code — engineering goes to Mario.
 */
import { useEffect, useRef, useState } from 'react'
import { Bot, Send, Ticket, Info, Trash2, History, Plus } from 'lucide-react'
import * as api from '../../api/client'
import BackToSupportTools from './BackToSupportTools'
import AssistantText from '../../components/workflow/AssistantText'
import { friendlyError } from '../../api/errors'
import { useAuth } from '../../auth/AuthContext'
import { useStickyState } from '../../components/useStickyState'

interface Msg { role: 'user' | 'assistant'; content: string; mode?: string; tools?: string[] }

// Two sets, because offering "How does the queue look right now?" to an assistant that cannot read
// the queue invites a question it must refuse. The prompts have to match the capability for the same
// reason the system prompt does.
const SUGGESTIONS_WITH_DATA = [
  'How does the queue look right now?',
  'Which tickets are breaching SLA?',
  'Any errors in the logs today?',
  'How is CPU across all pharmacies, and which is highest?',
  'A pharmacy says prescriptions are missing — where do I start?',
]
const SUGGESTIONS_KNOWLEDGE_ONLY = [
  'How do the SLA clocks work?',
  'What pauses the resolution clock?',
  'A pharmacy says prescriptions are missing — where do I start?',
  'When do I escalate to Mario?',
]

// D-SUPPORT-5: the assistant can now read real data. Showing WHICH lookups it ran turns an
// unverifiable claim into a checkable one — an agent who sees "searched the system log" can go
// look at the same log, and an answer with no lookups behind it is visibly general knowledge.
const TOOL_LABELS: Record<string, string> = {
  search_tickets: 'searched the ticket queue',
  get_ticket: 'read a ticket',
  queue_stats: 'checked queue stats',
  search_logs: 'searched the system log',
  platform_health: 'checked platform health',
  tenant_resource_metrics: 'checked per-pharmacy CPU / database health',
}

const btnStyle = (active: boolean): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 8,
  border: '1px solid var(--color-border)', background: active ? 'var(--color-bg)' : 'var(--color-surface)',
  color: 'var(--color-text-mute)', fontSize: 12.5, cursor: 'pointer',
})

function describeTools(tools: string[]): string {
  const seen: string[] = []
  for (const t of tools) {
    const label = TOOL_LABELS[t] ?? t
    if (!seen.includes(label)) seen.push(label)
  }
  return seen.join(' · ')
}

// D-SUPPORT-7: how many messages we keep. A transcript is unbounded and sessionStorage is not
// (~5MB); the hook degrades to in-memory on quota, but silently losing persistence is worse than
// deliberately keeping a recent window. 60 messages is ~30 exchanges — far more than a support
// agent revisits, and small enough that a couple of long get_ticket answers cannot fill the quota.
const MAX_PERSISTED_MSGS = 60

export default function SupportAiPage() {
  const { me, hasPermission } = useAuth()
  // Only a manager can act on the "switched off" note, so only a manager is told where.
  const isManager = hasPermission('support.settings.manage')

  // Persist the conversation so it survives navigating away and back, and an F5 — the actual
  // complaint. sessionStorage via useStickyState, NOT useDurableState, is deliberate and follows
  // that hook's own doctrine: a transcript can contain ticket text (pasted by the agent or returned
  // by get_ticket) and therefore patient content, so it must die with the tab AND be dropped at the
  // identity boundary. The key is account-scoped so two people sharing a workstation never see each
  // other's thread; `null` before /auth/me resolves means "don't persist yet" rather than writing
  // under a wrong key.
  const chatKey = me?.account_id ? `ai:chat:${me.account_id}` : null
  const [msgs, setMsgs] = useStickyState<Msg[]>(chatKey, [])
  const [input, setInput] = useState('')
  // The pinned ticket is part of the conversation's context, so it rides the same key and lifetime.
  const [ticketId, setTicketId] = useStickyState<string>(chatKey ? `${chatKey}:ticket` : null, '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // D-SUPPORT-7: the SERVER-side conversation this thread is being saved into. sessionStorage keeps
  // the in-progress thread across a screen switch; this id is what lets it be reopened after a
  // sign-out, a shift change, or from another machine — which per-tab storage cannot do.
  const [convId, setConvId] = useStickyState<string | null>(chatKey ? `${chatKey}:conv` : null, null)
  const [history, setHistory] = useStickyState<api.SupportAiConversation[]>(chatKey ? `${chatKey}:list` : null, [])
  const [showHistory, setShowHistory] = useState(false)
  const [loadingConv, setLoadingConv] = useState(false)

  const refreshHistory = () => {
    api.listSupportAiConversations().then(r => setHistory(r.conversations)).catch(() => {})
  }
  useEffect(() => { if (me?.account_id) refreshHistory() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [me?.account_id])

  const resume = async (id: string) => {
    setLoadingConv(true); setError(null)
    try {
      const c = await api.getSupportAiConversation(id)
      setMsgs(c.messages.map(m => ({ role: m.role, content: m.content, tools: m.tools ?? [], mode: m.mode })))
      setConvId(c.id)
      setTicketId(c.ticket_ref ?? '')
      setShowHistory(false)
      setTimeout(() => scroller.current?.scrollTo({ top: 1e9 }), 50)
    } catch (e) { setError(friendlyError(e)) }
    finally { setLoadingConv(false) }
  }

  // "New chat" only detaches from the saved conversation — it never deletes it. Losing an agent's
  // history because they wanted a fresh thread would be the worst possible reading of this button.
  const newChat = () => { setMsgs([]); setConvId(null); setTicketId(''); setError(null); setShowHistory(false) }

  const removeConv = async (id: string) => {
    try {
      await api.deleteSupportAiConversation(id)
      setHistory(h => h.filter(c => c.id !== id))
      if (convId === id) newChat()
    } catch (e) { setError(friendlyError(e)) }
  }
  const [caps, setCaps] = useState<api.SupportAiCapabilities | null>(null)
  const scroller = useRef<HTMLDivElement>(null)

  // Which lookups can actually return data here. In an environment with no Azure subscription (the
  // local demo stack) the CPU/database tool has nothing to read, and the assistant will say so — this
  // banner is what distinguishes "not wired up in THIS environment" from "the feature is broken",
  // which is the whole question when you are evaluating it before it ships.
  useEffect(() => { api.getSupportAiCapabilities().then(setCaps).catch(() => setCaps(null)) }, [])
  // Two distinct states, and conflating them would mislead: BLOCKED means the lookup returns nothing
  // here; DEGRADED means it works but part of its data source is missing (platform_health still has
  // live reliability counters without Azure, it just loses App Insights and latency).
  const blocked = (caps?.tools ?? []).filter(t => !t.available)
  const degraded = (caps?.tools ?? []).filter(t => t.available && t.reason)

  // Deliberate CONFIGURATION (an operator switched lookups off) is not the same as a broken or
  // half-wired ENVIRONMENT, and must not be dressed as a warning — in production that amber box read
  // as "this feature is broken" when nothing was wrong. Rendered as a quiet note instead, and the
  // header and prompts below drop their claims of data access so the page stops advertising
  // capabilities it does not currently have.
  const toolsOff = caps?.environment === 'tools_disabled'
  const suggestions = toolsOff ? SUGGESTIONS_KNOWLEDGE_ONLY : SUGGESTIONS_WITH_DATA

  const send = async (text?: string) => {
    const q = (text ?? input).trim()
    if (!q || busy) return
    setError(null)
    const userMsg: Msg = { role: 'user', content: q }
    const next: Msg[] = [...msgs, userMsg].slice(-MAX_PERSISTED_MSGS)
    setMsgs(next); setInput(''); setBusy(true)
    try {
      const r = await api.supportAiChat(
        next.map(m => ({ role: m.role, content: m.content })),
        ticketId.trim() || undefined,
        convId ?? undefined,
      )
      // The server creates the conversation on the first exchange and returns its id.
      if (r.conversation_id && r.conversation_id !== convId) setConvId(r.conversation_id)
      const aiMsg: Msg = { role: 'assistant', content: r.reply, mode: r.mode, tools: r.tools_used ?? [] }
      setMsgs(m => [...m, aiMsg].slice(-MAX_PERSISTED_MSGS))
      setTimeout(() => scroller.current?.scrollTo({ top: 1e9, behavior: 'smooth' }), 50)
      refreshHistory()
    } catch (e) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: '100%', margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 120px)' }}>
      <header style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <BackToSupportTools />
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Bot size={22} /> AI assistant</h1>
          <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>
            {toolsOff
              ? <>Product questions, user-experience help, troubleshooting guidance — answered from product
                  knowledge. It cannot look up live data in this deployment, and it cannot change anything;
                  engineering escalations go to Mario.</>
              : <>Product questions, user-experience help, troubleshooting guidance. It can read the ticket
                  queue and the system logs (read-only) — just mention a ticket number or describe the error.
                  It cannot change anything; engineering escalations go to Mario.</>}
          </div>
        </div>
        {/* The conversation persists across screens, so the agent needs a way to end it deliberately —
            both to start a clean thread and to drop a transcript that quoted a patient. Without this
            the only way to clear it is signing out. */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={() => setShowHistory(v => !v)} title="Saved conversations"
            style={btnStyle(showHistory)}>
            <History size={13} aria-hidden="true" /> History{history.length > 0 ? ` (${history.length})` : ''}
          </button>
          {msgs.length > 0 && (
            <button onClick={newChat} title="Start a new conversation (the current one stays saved)"
              style={btnStyle(false)}>
              <Plus size={13} aria-hidden="true" /> New chat
            </button>
          )}
        </div>
      </header>

      {/* Config-off: a quiet, neutral note. No amber, no "cannot reach", no bullet list of failures —
          nothing is failing. Managers get the pointer to the setting; agents just get the fact. */}
      {toolsOff && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12, padding: '8px 11px',
          borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)',
          color: 'var(--color-text-mute)', fontSize: 12.5, lineHeight: 1.5,
        }}>
          <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
          <div>
            Answering from product knowledge only — live data lookups (tickets, logs, health) are
            switched off for this deployment.
            {isManager && <> A support manager can turn them on in <strong>Support settings</strong>.</>}
          </div>
        </div>
      )}

      {!toolsOff && (blocked.length > 0 || degraded.length > 0) && (
        <div style={{
          display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 12, padding: '10px 12px',
          borderRadius: 9, border: '1px solid #fcd34d', background: '#fffbeb', color: '#78350f', fontSize: 12.5, lineHeight: 1.5,
        }}>
          <Info size={15} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
          <div>
            <strong>This environment cannot reach every data source.</strong> The assistant will correctly
            report the lookups below as unavailable — that is a property of this deployment, not a fault in
            the feature. Everything else is live and answering from real data.
            <ul style={{ margin: '7px 0 0', paddingLeft: 18 }}>
              {blocked.map(t => (
                <li key={t.name} style={{ marginBottom: 3 }}>
                  <strong>{t.label}</strong> — {t.reason}
                </li>
              ))}
              {degraded.map(t => (
                <li key={t.name} style={{ marginBottom: 3 }}>
                  <strong>{t.label}</strong> — partly live. {t.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <Ticket size={14} style={{ color: 'var(--color-text-mute)' }} />
        <input value={ticketId} onChange={e => setTicketId(e.target.value)}
               placeholder="Optional: pin a ticket id for context (or just say &quot;ticket 412&quot; in your question)"
               style={{ flex: 1, maxWidth: 420, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 12.5, background: 'var(--color-bg)', color: 'var(--color-text)' }} />
      </div>

      {showHistory && (
        <div style={{ marginBottom: 12, border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-surface)', overflow: 'hidden' }}>
          <div style={{ padding: '9px 12px', borderBottom: '1px solid var(--color-border)', fontSize: 12.5, fontWeight: 700 }}>
            Your saved conversations
            <span style={{ fontWeight: 400, color: 'var(--color-text-mute)' }}> — only you can see these</span>
          </div>
          {history.length === 0 ? (
            <div style={{ padding: '12px', fontSize: 12.5, color: 'var(--color-text-mute)' }}>
              No saved conversations yet. Ask something and it will be saved here automatically.
            </div>
          ) : (
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {history.map(c => (
                <div key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  borderTop: '1px solid var(--color-border)',
                  background: c.id === convId ? 'var(--color-bg)' : undefined,
                }}>
                  <button onClick={() => resume(c.id)} disabled={loadingConv}
                    style={{ flex: 1, textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-text)' }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.title}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 2 }}>
                      {c.message_count} message{c.message_count === 1 ? '' : 's'}
                      {c.ticket_ref ? ' · pinned to a ticket' : ''}
                      {' · '}{new Date(c.updated_at).toLocaleString()}
                    </div>
                  </button>
                  {c.id === convId && <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>current</span>}
                  <button onClick={() => removeConv(c.id)} title="Delete this conversation"
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-text-mute)', padding: 4 }}>
                    <Trash2 size={13} aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div ref={scroller} style={{ flex: 1, overflowY: 'auto', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
        {msgs.length === 0 ? (
          <div style={{ color: 'var(--color-text-mute)', fontSize: 13.5 }}>
            <div style={{ marginBottom: 10 }}>Ask anything about OwlRx. Try:</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
              {suggestions.map(s => (
                <button key={s} onClick={() => send(s)}
                        style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', borderRadius: 999, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : msgs.map((m, i) => (
          <div key={i} style={{ marginBottom: 12, display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              // pre-wrap only for the USER's own text. The assistant's reply is markdown and is
              // rendered by AssistantText — leaving it pre-wrap is what printed "**17 tickets**"
              // with the asterisks showing.
              maxWidth: '85%', padding: '9px 13px', borderRadius: 12, fontSize: 13.5,
              whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal',
              background: m.role === 'user' ? 'var(--color-primary, #0f766e)' : 'var(--color-bg)',
              color: m.role === 'user' ? '#fff' : 'var(--color-text)',
              border: m.role === 'user' ? 'none' : '1px solid var(--color-border)',
            }}>
              {m.mode === 'demo' && <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.4px', color: '#b45309', marginBottom: 4 }}>DEMO KNOWLEDGE BASE</div>}
              {m.role === 'assistant' ? <AssistantText text={m.content} /> : m.content}
              {m.role === 'assistant' && m.tools && m.tools.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--color-text-mute)', marginTop: 7, paddingTop: 6, borderTop: '1px dashed var(--color-border)' }}>
                  Looked at: {describeTools(m.tools)}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && <div style={{ color: 'var(--color-text-mute)', fontSize: 13 }}>Thinking…</div>}
      </div>

      {error && <div style={{ color: '#b91c1c', fontSize: 12.5, marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)}
               onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send() } }}
               placeholder="Ask about the product, a user problem, or troubleshooting…"
               style={{ flex: 1, padding: '11px 13px', borderRadius: 10, border: '1px solid var(--color-border)', fontSize: 13.5, background: 'var(--color-surface)', color: 'var(--color-text)' }} />
        <button onClick={() => void send()} disabled={busy || !input.trim()}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 16px', borderRadius: 10, border: 'none', background: 'var(--color-primary, #0f766e)', color: '#fff', fontSize: 13.5, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy || !input.trim() ? 0.6 : 1 }}>
          <Send size={14} /> Send
        </button>
      </div>
    </div>
  )
}
